import React, { useRef } from "react"
import { Question, section } from "../../global-interfaces";
import { rollups } from "d3-array";

import "./Card.scss";
import Title from "../Title"
import Annotation from "../Annotation"
import Horizontal from "../Horizontal";
import PieChart from "../PieChart";
import PieLegend from "../PieLegend";
import WordCloud from "../WordCloud";
import VerticalMany from "../VerticalMany";
import VerticalTwo from "../VerticalTwo";

import { CHART_TYPES as CT } from "../../constants";
import { generateCardID } from "../../utils"

/** INTERFACES */
interface CardProps {
  data: Question[];
  section: section;
  observer: IntersectionObserver;
  isMobile: boolean;
}

interface Graph {
  title: boolean;
  chart_type: string;
  data_objects: Question[]
}

export default function Card({ data, section, observer, isMobile }: CardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  if (cardRef.current) observer.observe(cardRef.current); 

  const rowItems = rollups(data, v => ({ 
    title: !!v[0].graph_title,
    chart_type: v[0].chart_type, 
    data_objects: v 
  }), d => d.graph_title, d => d.groupById)

  type GraphLevelItem = [string, Graph] // [groupById, data]
  type TitleLevelItem = [string, GraphLevelItem[]] // [title, graphs[]]
  
  return (
    <div className="Row" id={generateCardID(section, 1)} ref={cardRef}>
      {rowItems.map(([title, graphs] : TitleLevelItem) => <div className="RowItem">
        {title && <Title title={title} isMobile={isMobile}/>}
        <div className="Card">
          {graphs.map(([id, graph]) => Graph(graph, section.color))}
        </div>
      </div>)}
    </div>
  )
}

function Graph(graph: Graph, color : string) {
  const { chart_type, data_objects } = graph
  const [ primary, secondary ] = data_objects

  switch (chart_type) {
    case CT.VERT:
      if (primary.data.length > 2) return (
        <VerticalMany chart={primary} color={color} />);
      else return (<VerticalTwo chart={primary} color={color} />)
              
    case CT.PIE:
      return (
        <PieChart
          chart={{ ...primary, data: primary.data.sort((a: any, b: any) => b.value - a.value) }}
          color={color}
        />
      );

    case CT.PIE_LEGEND:
      return (<PieLegend chart={primary} color={color}/>)

    case CT.HORIZ:
      return <Horizontal 
        primary={primary} 
        secondary={secondary} 
        color={color} />;

    case CT.INLINE_HORIZ:
      return <Horizontal 
        primary={primary} 
        secondary={secondary} 
        color={color} 
        inline={true} />;

    case CT.ANNOTATION:
      return <Annotation annotation={primary.data} />;

    case CT.WORD:
      return <WordCloud words={primary.data.map(d => d.label)} color={color} />;

    default:
      console.log("CHART ERROR:", graph)
      return null
  }
}
